import {ChangeDetectionStrategy, Component, effect, inject, ViewEncapsulation} from '@angular/core';
import {TranslocoDirective} from '@jsverse/transloco';
import {MessageService} from '@openng/optimus-ui/api';
import {ExclamationTriangleIcon} from '@openng/optimus-ui/icons';
import {Toast, ToastModule} from '@openng/optimus-ui/toast';
import {ErrorService} from '@scalified-monorepo/ng-shared/errors';

const NETWORK_RETRY_TOAST_KEY = 'network-retry';

/**
 * Drop-in component that shows a single persistent toast while the retry interceptor
 * is reconnecting to the backend, updating the attempt counter in real time.
 *
 * Requires `MessageService` to be provided in the host app (typically in AppComponent providers).
 *
 * @example
 * ```html
 * <!-- app.component.html -->
 * <p-toast ...></p-toast>
 * <network-retry-toast></network-retry-toast>
 * <router-outlet></router-outlet>
 * ```
 */
@Component({
  selector: 'network-retry-toast',
  standalone: true,
  imports: [ToastModule, Toast, ExclamationTriangleIcon, TranslocoDirective],
  template: `
    @if (retryingState().active) {
      <div class="fixed inset-0 z-1103 bg-black/30 backdrop-blur-sm" aria-hidden="true"></div>
    }
    <p-toast
      [key]="NETWORK_RETRY_TOAST_KEY"
      [baseZIndex]="1200"
      [breakpoints]="{'920px': {width: 'calc(100% - 20px)', right: '10px', left: '10px'}}"
      position="bottom-right">
      <ng-template #message>
        <ng-container *transloco="let transl">
          <svg data-p-icon="exclamation-triangle" class="p-toast-message-icon" aria-hidden="true"></svg>
          <div class="p-toast-message-text">
            <div class="p-toast-summary">{{ transl('_shared.network_retry_summary') }}</div>
            <div class="p-toast-detail">
              {{
                transl('_shared.network_retry_detail', {
                  attempt: retryingState().attempt,
                  maxAttempts: retryingState().maxAttempts,
                })
              }}
            </div>
          </div>
        </ng-container>
      </ng-template>
    </p-toast>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkRetryToastComponent {
  protected readonly NETWORK_RETRY_TOAST_KEY = NETWORK_RETRY_TOAST_KEY;
  protected readonly retryingState = inject(ErrorService).retryingState;

  private readonly messageService = inject(MessageService);

  constructor() {
    let toastShown = false;
    effect(() => {
      const state = this.retryingState();
      if (state.active) {
        if (!toastShown) {
          toastShown = true;
          this.messageService.add({
            key: NETWORK_RETRY_TOAST_KEY,
            severity: 'warn',
            sticky: true,
            closable: false,
          });
        }
      } else {
        toastShown = false;
        this.messageService.clear(NETWORK_RETRY_TOAST_KEY);
      }
    });
  }
}
